import useNavigationStore from '@src/stores/navigationStore';
import useRegistrationStore from '@src/stores/registrationStore';

import styles from './USPerson.module.scss';
import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import BackButton from '@src/components/_shared/BackButton';
import Container from '@src/components/_shared/Container';
import AmericanFlag from '@src/assets/AmericanFlag.png';
import ValidateChoiceButton from '@src/components/_shared/UI/ValidateChoiceButton';
import { useState } from 'react';
import WarningModal from '@src/components/_shared/WarningModal';
import VirtualAssistant from '@src/assets/VirtualAssistant.png';
import { useMutation } from '@tanstack/react-query';
import { personCreation, personCreationFromPartner, personUpdate } from '@src/api/Registration';
import Loader from '@src/components/_shared/UI/Loader';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import { Toaster } from 'react-hot-toast';
import { UserRoleType } from '@src/utilities/types';
import { authUserRoles } from '@src/api/Auth';
import { handleApiError } from '@src/utilities/functions';

export default function USPerson() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();

    const [showModal, setShowModal] = useState<boolean>(false);

    const { mutate: mutateUSPerson, isPending: isMutateUSPersonPending } = useMutation({
        mutationFn: () => personUpdate({key: 'us_person', value: registration.person.us_person, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id}),
        onSuccess: async() => {
            if (!registration.partner_informations.is_partner) {
                const userRole = await authUserRoles();
                setField('user_role', userRole.name);
            }
            setBlock('LegalAgentSituation');
        },
        onError: (error: any) => handleApiError(error)
    });

    // si psi 
    const { mutate: mutateCreationFromPartner, isPending: isMutateCreationFromPartnerPending } = useMutation({
        mutationFn: () => personCreationFromPartner({
            civility: registration.person.basic.civility,
            email: registration.person.basic.email,
            first_name: registration.person.basic.firstname,
            last_name: registration.person.basic.lastname,
            birth_name: registration.person.basic.birthname,
            mobile_phone: registration.person.basic.phone,
            sitecode: registration.partner_informations.brand.sitecode
        }),
        onSuccess: (data: {person_id: string}) => {
            setField('person_id', data.person_id);
            mutateUSPerson();
        },
        onError: (error: any) => handleApiError(error)
    });

    const { mutate, isPending } = useMutation({
        mutationFn: () => personCreation(), 
        onSuccess: () => {
            mutateUSPerson()
        },
        onError: (error: any) => handleApiError(error)
    });

    const _onGoBack = () => {
        setSection(1)
        setBlock('Register')
    };

    const _onValidate = () => {
        setField('person.us_person', {
            us_citizen: false,
            us_resident: false,
            has_green_card: false,
            us_taxpayer: false
        })

        if (registration.user_role === UserRoleType.Premium) {
            mutateUSPerson()
        } else {
            if (registration.partner_informations.is_partner) {
                if (registration.person_id) {
                    mutateUSPerson()
                } else {
                    mutateCreationFromPartner()
                }
            } else {
                mutate()
            }
        }
    };

    const _isAmericanCitizen = () => {
        setShowModal(true)
        setField('person.us_person', {
            us_citizen: true,
            us_resident: true,
            has_green_card: true,
            us_taxpayer: true
        })
    }

    return (
        <Container>
            <BackButton onClick={_onGoBack}/>
            <BlockTitle title="Êtes-vous dans un des cas suivants ?" />
            <Subtitle content="Cela nous permet d’ouvrir votre compte dans le respect des réglementations en vigueur."/>
            <div className={styles.contentContainer}>
                <div className={styles.flagContainer}>
                    <img src={AmericanFlag} alt="AmericanFlag" className={styles.flag} />
                    <p className={styles.text}>Résident américain</p>
                </div>
                <div className={styles.flagContainer}>
                    <img src={AmericanFlag} alt="AmericanFlag" className={styles.flag} />
                    <p className={styles.text}>Citoyen américain</p>
                </div>
                <div className={styles.flagContainer}>
                    <img src={AmericanFlag} alt="AmericanFlag" className={styles.flag} />
                    <p className={styles.text}>Contribuable américain</p>
                </div>
                <div className={styles.flagContainer}>
                    <img src={AmericanFlag} alt="AmericanFlag" className={styles.flag} />
                    <p className={styles.text}>Détenteur de Green Card</p>
                </div>
            </div>

            {isMutateUSPersonPending || isMutateCreationFromPartnerPending || isPending ?
                <Loader type="SPINNER" />
                :
                <div className={styles.buttons}>
                    <ValidateChoiceButton title="OUI" onValidate={_isAmericanCitizen} />
                    <ValidateChoiceButton title="NON" onValidate={_onValidate} />
                </div>
            }
            <Toaster/>
            {showModal ?
                <WarningModal
                    image={VirtualAssistant}
                    show={showModal}
                    onValidate={() => setShowModal(false)}
                    content={`Vous êtes malheureusement considérée comme une "US Person" au sens de la législation américaine, et ne pouvez donc pas devenir client ${registration.partner_informations.is_partner ? 'Direct Securities' : 'Bourse Direct'}. Nous en sommes désolés et vous prions de contacter les services administratifs concernés pour plus d’informations`}
                />
                : null}
        </Container>
    );
}
